// Lines of a subject without a conversation (a compare) sent to a
// connected agent from the Files view's selection ("Ask agent", in the
// selection's bar): a range task (api/relayTask.ts
// `changeling.review-range.v1`) with no thread, which needs `files`
// granted. A pull request's lines go to an agent through a review thread
// instead (logic/threadTask.ts), which carries the thread's comments.
//
// The lines are named by content, as a thread's are: the side (`old`
// before the change, `new` after it), git's object ID of that side's file
// at the commit the Files view read it at, and that commit. The task goes
// under a topic of its own lines, `lines <digest> <id>` (api/relayTask.ts
// `linesTopic`), so each range has its own conversation with the agent, and
// the same lines asked again find it.
import { linesTopic, RANGE_TASK, type TaskBody } from '../api/relayTask';
import type { SubjectKey } from '../api/relaySubject';
import { sha256Hex } from './sha256';
import { GITHUB_READS, type RangeReads } from './threadTask';

/** What a range ask is sent with unless the person writes another request. */
export const RANGE_REQUEST = 'Review these lines: say what is wrong or risky in them, and what you would change.';

/** The lines asked about, as the Files view has them: the file, the side, the first and last line there, and the commit that side is read at. */
export interface AskedLines {
  path: string;
  side: 'old' | 'new';
  start: number;
  end: number;
  commit: string;
}

/** The topic of `lines` of `subject`: one per range of lines (see the file comment). */
export function rangeTopic(subject: SubjectKey, lines: AskedLines): string {
  const id = sha256Hex(new TextEncoder().encode([lines.path, lines.side, lines.start, lines.end, lines.commit].join('\0'))).slice(0, 24);
  return linesTopic(subject, id);
}

/**
 * What asking about `lines` of `subject` (its head `head`) with
 * `instruction` carries: where they are, by content, and the request.
 * Throws when the file can't be read at the commit.
 */
export async function rangeTask(subject: SubjectKey, head: string, lines: AskedLines, instruction: string, owner: string, repo: string, reads: RangeReads = GITHUB_READS): Promise<TaskBody> {
  const blob = await reads.blobAt(owner, repo, lines.commit, lines.path);
  if (blob === null) throw new Error(`Changeling could not read ${lines.path} at ${lines.commit.slice(0, 7)}, where these lines are.`);
  return {
    kind: RANGE_TASK,
    payload: {
      subject,
      head,
      path: lines.path,
      side: lines.side,
      blob,
      start_line: Math.min(lines.start, lines.end),
      end_line: Math.max(lines.start, lines.end),
      commit: lines.commit,
      instruction,
    },
  };
}
